/**
 * Iter-89 Phase 2 — Dedicated CEO/ADMIN page listing users whose CRA for the
 * previous month was not submitted by the cut-off (2nd business day of the
 * current month). Reachable from the dashboard "CRA en retard" indicator.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowLeft, AlertTriangle, Mail, Send, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MONTH_FR = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function CraLatePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get("/dashboard/cra-late")
      .then((r) => setData(r.data))
      .catch(() => setData({ late_users: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const onEscalate = async () => {
    setSending(true);
    try {
      const r = await api.post("/dashboard/cra-late/escalate");
      const sent = r.data?.sent ?? 0;
      const failures = r.data?.failures ?? 0;
      if (sent > 0) {
        toast.success(`Relance envoyée à ${sent} utilisateur${sent > 1 ? "s" : ""}` + (failures ? ` (${failures} échec${failures > 1 ? "s" : ""})` : ""));
      } else {
        toast.info(r.data?.message || "Aucun email envoyé");
      }
      setConfirmOpen(false);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-zinc-500" data-testid="cra-late-loading">Chargement…</div>;
  }
  const late = data?.late_users || [];
  const past = data?.business_days_past_cutoff ?? 0;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="cra-late-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3" data-testid="cra-late-back">
        <ArrowLeft size={14} /> Retour au dashboard
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-red-50 text-red-700 flex items-center justify-center">
          <AlertTriangle size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">CRA EN RETARD</div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight" data-testid="cra-late-title">
            {data ? `${MONTH_FR[data.month] || ""} ${data.year || ""}` : ""}
          </h1>
        </div>
        {late.length > 0 && (
          <button
            type="button"
            data-testid="cra-late-escalate-btn"
            onClick={() => setConfirmOpen(true)}
            disabled={sending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-navy text-white text-sm font-medium hover:bg-brand-navy/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Relancer maintenant
          </button>
        )}
      </div>

      {data?.cutoff_date && (
        <div className="text-xs text-zinc-600 font-mono mb-4">
          Date limite : <span className="text-brand-navy">{data.cutoff_date}</span>
          {past > 0 && <> · <strong>{past} jour{past > 1 ? "s" : ""} ouvré{past > 1 ? "s" : ""} de retard</strong></>}
          {past >= 5 && <span className="ml-2 px-2 py-0.5 rounded text-[10px] bg-red-100 text-red-700 font-bold">Escalade automatique active</span>}
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
          <div className="col-span-4">Utilisateur</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-4">Manager(s) CC</div>
        </div>
        {late.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-zinc-500" data-testid="cra-late-empty">
            Aucun utilisateur en retard.
          </div>
        )}
        {late.map((u) => (
          <div key={u.user_id} data-testid={`cra-late-row-${u.user_id}`} className="grid grid-cols-12 px-5 py-3 text-sm border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50">
            <div className="col-span-4 font-medium text-brand-navy">{u.name || "—"}</div>
            <div className="col-span-4 text-xs font-mono text-zinc-700 truncate flex items-center gap-1">
              <Mail size={12} className="opacity-50" />{u.email}
            </div>
            <div className="col-span-4 text-xs font-mono text-zinc-600 truncate">
              {(u.manager_emails || []).length > 0 ? u.manager_emails.join(", ") : <span className="text-zinc-400 italic">—</span>}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-zinc-500 mt-4 max-w-prose">
        Une <strong>relance email automatique</strong> est envoyée à chaque utilisateur encore en retard
        à <strong>5 jours ouvrés</strong> après la date limite, avec son manager en copie.
        Vous pouvez aussi déclencher une relance manuelle depuis la page CRA Validation.
      </p>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="cra-late-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Relancer maintenant ?</AlertDialogTitle>
            <AlertDialogDescription>
              {`Vous êtes sur le point d'envoyer ${late.length} email${late.length > 1 ? "s" : ""} de relance pour le CRA de ${data ? MONTH_FR[data.month] : ""} ${data?.year || ""}, avec le ou les managers en copie.`}
              <br /><br />
              <strong>Cet envoi est unique pour la période</strong> — la relance automatique du J+5 ne sera pas déclenchée à nouveau.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending} data-testid="cra-late-confirm-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onEscalate(); }}
              disabled={sending}
              data-testid="cra-late-confirm-send"
              className="bg-brand-navy hover:bg-brand-navy/90"
            >
              {sending ? "Envoi…" : "Confirmer l'envoi"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
